import * as Sentry from '@sentry/react'
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  isComparatorConsentValueMissing,
  isPractitionerMediatedConsentFlow,
  isPrimaryConsentFlow,
  resolveUserConsentFlow,
} from '../../../utils/consentFlowHelper'
import FamilyPortalAPIClient from '../../auth/FamilyPortalAPIClient'
import { useFamilyPortalApiClient } from '../../auth/hooks/useFamilyPortalApiClient'
import { usePreferencesContext } from '../../preferences/usePreferencesContext'
import type { ConsentContext } from '../consentOptions/types'
import { subjectRows as buildSubjectRows, scopedStateKey, secondaryAnswerSubjects } from '../consentSubjects'

type EntryPointRouting = { loading: true; redirectTo: null } | { loading: false; redirectTo: string | null }

const SUBJECT_SUFFIXES = [':SELF', ':DEPENDENT']

function hasOwn(map: object | undefined, key: string): boolean {
  return map != null && Object.prototype.hasOwnProperty.call(map, key)
}

function hasSecondaryShapeMismatch(state: ConsentContext['secondaryConsentState'], dualSecondary: boolean): boolean {
  if (!state) return false
  const keys = [
    ...Object.keys(state.itemStates ?? {}),
    ...Object.keys(state.itemMetadata ?? {}),
    ...Object.keys(state.itemElections ?? {}),
  ].filter((k) => !k.startsWith('ACCOUNT_PREF_'))
  if (keys.length === 0) return false
  const hasScopedKeys = keys.some((k) => SUBJECT_SUFFIXES.some((suffix) => k.endsWith(suffix)))
  const hasBareKeys = keys.some((k) => !SUBJECT_SUFFIXES.some((suffix) => k.endsWith(suffix)))
  return dualSecondary ? hasBareKeys : hasScopedKeys
}

function isModernSecondaryConsentComplete(
  context: ConsentContext,
  state: NonNullable<ConsentContext['secondaryConsentState']>,
  dualSecondary: boolean,
  requiredItemIds: readonly string[]
): boolean {
  const answerSubjects = secondaryAnswerSubjects(context, dualSecondary)
  if (answerSubjects) {
    return requiredItemIds.every((itemId) =>
      buildSubjectRows(itemId, answerSubjects, dualSecondary).every(({ subject, key }) =>
        subject.kind === 'sibling'
          ? hasOwn(state.subjectItemStates?.[subject.familyMemberId], itemId)
          : hasOwn(state.itemElections, key)
      )
    )
  }

  const scopes: Array<'SELF' | 'DEPENDENT' | undefined> = dualSecondary ? ['SELF', 'DEPENDENT'] : [undefined]
  return requiredItemIds.every((itemId) =>
    scopes.every((scope) => hasOwn(state.itemElections, scopedStateKey(itemId, scope)))
  )
}

function isSecondaryConsentComplete(context: ConsentContext): boolean {
  const state = context.secondaryConsentState
  const requiredItemIds = context.requiredSecondaryConsentItemIds
  // An empty server catalog means there are no secondary questions to answer.
  if (requiredItemIds?.length === 0) return true
  const dualSecondary = context.secondaryConsentDualSubjects === true
  // During a staggered deploy, an older API cannot authoritatively describe the
  // modern catalog. Fail open instead of trapping the user in a redirect loop.
  if (!state) return requiredItemIds === undefined
  // Fail-open: shape mismatches are not evaluable and must never force a lockout redirect.
  if (hasSecondaryShapeMismatch(state, dualSecondary)) return true
  const hasModernElectionState =
    Object.keys(state.itemElections ?? {}).length > 0 && Object.keys(state.itemScopes ?? {}).length > 0
  if (hasModernElectionState) {
    if (!requiredItemIds) return true
    return isModernSecondaryConsentComplete(context, state, dualSecondary, requiredItemIds)
  }

  const elections = state.itemElections ?? {}
  const metadata = state.itemMetadata ?? {}
  const requiredKeys = Object.keys(state.itemStates ?? {}).filter((k) => !k.startsWith('ACCOUNT_PREF_'))
  if (requiredKeys.length === 0) return false
  return requiredKeys.every((k) => elections[k] === 'unset' || k in metadata)
}

async function resolveOnboardingRedirect(
  apiClient: FamilyPortalAPIClient,
  needsLanguage: boolean
): Promise<string | null> {
  if (needsLanguage) return '/language-select'

  try {
    const res = await apiClient.get('/profile/demographics')
    if (!res.ok) return '/dob-capture'
    const { dateOfBirth }: { dateOfBirth?: string | null } = await res.json()
    return !dateOfBirth ? '/dob-capture' : null
  } catch {
    return '/dob-capture'
  }
}

/** Re-resolve routing after leaving onboarding so consent completion is picked up. */
export function shouldRefetchEntryPointRouting(
  previousPathname: string | null,
  pathname: string,
  pathnameChanged: boolean
): boolean {
  if (previousPathname === null) return true
  if (!pathnameChanged) return true
  const wasOnboarding = previousPathname === '/intro' || previousPathname.startsWith('/consents')
  const isOnboarding = pathname === '/intro' || pathname.startsWith('/consents')
  return wasOnboarding && !isOnboarding
}

async function resolveEntryPointRedirect(
  apiClient: FamilyPortalAPIClient,
  needsLanguage: boolean
): Promise<string | null> {
  const onboarding = await resolveOnboardingRedirect(apiClient, needsLanguage)
  if (onboarding) return onboarding

  let isFirstLogin = true

  try {
    const firstLoginRes = await apiClient.get('/auth/check-first-login', undefined, true)
    if (firstLoginRes.ok) {
      const data: { isFirstLogin: boolean; lastLogin: string | null; loginsCount: number } = await firstLoginRes.json()
      isFirstLogin = data.isFirstLogin
    }
  } catch {
    isFirstLogin = true
  }

  try {
    const contextRes = await apiClient.get('/consents/context', undefined, true)

    if (contextRes.status === 404 || !contextRes.ok) {
      return isFirstLogin ? '/intro' : null
    }

    const context: ConsentContext = await contextRes.json()
    const { primaryConsentId, primaryConsentSignedAt } = context
    if (isComparatorConsentValueMissing(context)) {
      Sentry.captureMessage('useEntryPointRouting: comparator is missing platformCollectsConsentToTest', 'error')
    }
    // Per-user flow: a comparator follows their own per-order value, not the order mode.
    const consentFlow = resolveUserConsentFlow(context)
    const practitionerMediated = isPractitionerMediatedConsentFlow(consentFlow)
    const cttSigned = primaryConsentId != null || primaryConsentSignedAt != null

    if (!cttSigned && !practitionerMediated) {
      return '/intro'
    }

    if (practitionerMediated) {
      if (isFirstLogin) return '/intro'
      const secondaryComplete = isSecondaryConsentComplete(context)
      return secondaryComplete ? null : '/consents/secondary'
    }

    if (!isPrimaryConsentFlow(consentFlow)) {
      Sentry.captureMessage(`useEntryPointRouting: unrecognized consentFlow "${String(consentFlow)}"`, 'warning')
      return isFirstLogin ? '/intro' : null
    }

    const secondaryComplete = isSecondaryConsentComplete(context)
    return secondaryComplete ? null : '/consents/secondary'
  } catch {
    return isFirstLogin ? '/intro' : null
  }
}

export function useEntryPointRouting(): EntryPointRouting {
  const apiClient = useFamilyPortalApiClient()
  const { pathname } = useLocation()
  const { state: preferences } = usePreferencesContext()
  const [routing, setRouting] = useState<EntryPointRouting>({ loading: true, redirectTo: null })
  const previousPathnameRef = useRef<string | null>(null)
  const resolveGenerationRef = useRef(0)
  const lastResolvedRef = useRef<Extract<EntryPointRouting, { loading: false }>>({ loading: false, redirectTo: null })

  const needsLanguage = preferences.status === 'ready' && preferences.available === true && preferences.stored === null

  useEffect(() => {
    const previousPathname = previousPathnameRef.current
    const pathnameChanged = previousPathname !== null && previousPathname !== pathname
    const leavingOnboarding =
      pathnameChanged &&
      (previousPathname === '/intro' || previousPathname?.startsWith('/consents')) &&
      !(pathname === '/intro' || pathname.startsWith('/consents'))
    previousPathnameRef.current = pathname

    if (!shouldRefetchEntryPointRouting(previousPathname, pathname, pathnameChanged)) {
      setRouting((prev) => (prev.loading ? lastResolvedRef.current : prev))
      return
    }

    const generation = ++resolveGenerationRef.current
    setRouting({ loading: true, redirectTo: null })

    resolveEntryPointRedirect(apiClient, needsLanguage)
      .then((redirectTo) => {
        if (generation !== resolveGenerationRef.current) return
        const next = {
          loading: false,
          redirectTo: leavingOnboarding && redirectTo === '/intro' ? null : redirectTo,
        } as const
        lastResolvedRef.current = next
        setRouting(next)
      })
      .catch(() => {
        if (generation !== resolveGenerationRef.current) return
        const next = { loading: false, redirectTo: null } as const
        lastResolvedRef.current = next
        setRouting(next)
      })

    return undefined
  }, [apiClient, needsLanguage, pathname])

  // When transitioning out of the onboarding flow (e.g. /consents/* → /home), there is one
  // render where routing still holds the stale redirectTo from the previous resolution but the
  // new effect hasn't fired yet to reset it to loading. The guard would see redirectTo='/intro'
  // and immediately navigate before gen+1 can resolve. Detect this synchronously during render
  // by reading the ref (which still holds the previous pathname) and override to loading=true so
  // the guard shows a spinner instead.
  const prevPathname = previousPathnameRef.current
  const pathnameChangedNow = prevPathname !== null && prevPathname !== pathname
  if (pathnameChangedNow && !routing.loading) {
    const wasOnboarding = prevPathname === '/intro' || prevPathname!.startsWith('/consents')
    const isOnboarding = pathname === '/intro' || pathname.startsWith('/consents')
    if (wasOnboarding && !isOnboarding) {
      return { loading: true, redirectTo: null }
    }
  }

  return routing
}
